import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Tabs, TextareaAutosize } from '@mui/material';
import { HEADER_HEIGHT } from '../../../constants/constants';
import { Image } from '@mui/icons-material';

// ============================================
// Container & Layout
// ============================================
export const PageContainer = styled(Box)({
  background: 'white',
  borderRadius: '5px',
  width: '100%',
});

export const Divider = styled('hr')({
  margin: '22px 0',
  border: '1px solid #e1e1e1',
});

export const DividerSmall = styled(Box)({
  margin: '12px 0px',
});

// ============================================
// Header
// ============================================
export const HeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const BackButton = styled(Box)({
  textDecoration: 'none',
  color: '#3a3a3a',
  display: 'flex',
  cursor: 'pointer',
});

export const HeaderTitle = styled(Typography)({
  fontSize: '18px',
  marginLeft: '8px',
  fontWeight: 550,
});

export const ListLink = styled(Box)({
  color: '#0F7F85',
  fontSize: '14px',
  fontWeight: 550,
  textDecoration: 'none',
  cursor: 'pointer',
});

// ============================================
// User Name
// ============================================
export const UserNameContainer = styled(Box)({
  display: 'flex',
});

export const UserName = styled(Typography)({
  fontWeight: 'bold',
  fontSize: '22px',
  marginBottom: '23px',
});

// ============================================
// Tabs — 테이블 헤더 셀 스타일 (전체 폭 균등 분할).
// 페이지 스크롤 시 글로벌 AppBar 바로 아래에 sticky로 고정.
// ============================================
export const TabsContainer = styled(Box)({
  width: '100%',
  position: 'sticky',
  top: `${HEADER_HEIGHT}px`,
  // 콘텐츠가 비치지 않도록 opaque 배경.
  background: '#fff',
  // 스크롤되는 콘텐츠 위에 떠 있어야 하지만 모달(zIndex 1300+)보다는 아래.
  zIndex: 10,
});

export const StyledTabs = styled(Tabs)({
  width: '100%',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  overflow: 'hidden',
  minHeight: '44px',
  '& .MuiTabs-flexContainer': {
    width: '100%',
  },
  '& .MuiTab-root': {
    flex: 1,
    // MUI Tab은 기본 max-width 264px이라 flex:1이 안 먹힌다 — 해제 필요.
    maxWidth: 'none',
    minHeight: '44px',
    padding: '0 12px',
    background: '#FAFBFC',
    borderRight: '1px solid #e0e0e0',
    borderRadius: 0,
    color: '#666',
    fontSize: '18px',
    fontWeight: 600,
    textTransform: 'none',
    // 탭 클릭 시 배경/글자색이 부드럽게 전환되도록.
    transition: 'background-color 0.25s ease, color 0.25s ease',
    '&:last-of-type': {
      borderRight: 'none',
    },
    // 호버 시 미세한 강조.
    '&:hover:not(.Mui-selected)': {
      background: '#F0F4F5',
      color: '#333',
    },
  },
  '& .MuiTab-root.Mui-selected': {
    background: '#4AB3BC',
    color: '#fff',
    fontWeight: 600,
  },
  // 선택된 탭이 솔리드 배경으로 강조되므로 하단 indicator는 중복이라 숨긴다.
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

// ============================================
// User Info Card
// 회원 프로필 히어로 카드: 이름이 가장 크게 시선을 잡고,
// 우측 상단에 부드러운 teal radial glow로 액센트, 메타 정보는 아이콘 + 텍스트.
// CTA 버튼은 그라데이션 + 호버 시 살짝 떠오르는 모션으로 강조.
// ============================================
// 카드 + 이용 정보 통계를 가로로 배치하는 컨테이너.
export const UserInfoWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  gap: '16px',
  width: '100%',
  marginTop: '4px',
});

// 헤더 전용 stats 컨테이너 — 남는 폭을 3개 카드가 균등 분할.
export const UserInfoStats = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  gap: '12px',
  '& > *': {
    flex: 1,
    minWidth: 0,
  },
});

// ============================================
// 헤더 메모 (인라인 미리보기 + "수정하기")
// 본문은 클램프해 카드 높이를 고정하고, 편집은 MemoEditModal에서 한다.
// ============================================
export const MemoPreviewCard = styled(Box)({
  flex: '0 0 20%',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '16px 18px',
  background: '#fff',
  border: '1px solid #ebebeb',
  borderRadius: '8px',
});

export const MemoPreviewText = styled(Typography)({
  flex: 1,
  minHeight: 0,
  fontSize: '14px',
  color: '#4B5563',
  whiteSpace: 'pre-wrap',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
});

export const MemoPreviewPlaceholder = styled(MemoPreviewText)({
  color: '#A6A6A6',
});

export const MemoEditButton = styled(Button)({
  alignSelf: 'flex-end',
  minWidth: 0,
  height: '26px',
  padding: '0 12px',
  borderRadius: '13px',
  fontSize: '12px',
  fontWeight: 700,
  color: '#4AB3BC',
  background: '#FFFFFF',
  border: '1px solid #4AB3BC',
  '&:hover': {
    backgroundColor: '#4AB3BC',
    color: '#FFFFFF',
  },
});

// ============================================
// 메모 크게보기 모달
// ============================================
export const MemoModalTextarea = styled(TextareaAutosize)({
  width: '100%',
  borderRadius: '5px',
  border: '1px solid #e1e1e1',
  padding: '12px 14px',
  fontSize: '14px',
  fontFamily: 'inherit',
  resize: 'none',
  '&:focus': {
    outline: 'none',
    borderColor: '#4AB3BC',
    borderWidth: '2px',
  },
});

export const MemoModalMeta = styled(Typography)({
  marginTop: '8px',
  textAlign: 'right',
  fontSize: '12px',
  color: '#A6A6A6',
});

// ============================================
// 다중 메모 모달
// ============================================
export const MemoLayoutRow = styled(Box)({
  display: 'flex',
  gap: '24px',
  height: '450px',
});

export const MemoInputSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  flex: '0 0 340px',
  height: '100%',
});

export const MemoListHeader = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#555',
  paddingBottom: '10px',
  borderBottom: '1px solid #e0e0e0',
  marginBottom: '12px',
});

export const MemoList = styled(Box)({
  flex: 1,
  minWidth: 0,
  height: '100%',
  overflowY: 'auto',
  borderLeft: '1px solid #e0e0e0',
  paddingLeft: '24px',
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '2px' },
});

export const MemoItem = styled(Box)({
  position: 'relative',
  padding: '14px 16px',
  marginBottom: '10px',
  backgroundColor: '#fff',
  borderRadius: '10px',
  border: '1px solid #e8e8e8',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:hover': {
    borderColor: '#c0c0c0',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  },
});

export const MemoItemActions = styled(Box)({
  display: 'flex',
  gap: '6px',
  marginTop: '10px',
  paddingTop: '8px',
  borderTop: '1px solid #f0f0f0',
});

export const MemoActionButton = styled(Button)(({ variant }) => ({
  minWidth: 0,
  height: '28px',
  padding: '0 12px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'none',
  ...(variant === 'edit' && {
    color: '#0F7F85',
    backgroundColor: '#E8F5F5',
    '&:hover': { backgroundColor: '#D0EBEC' },
  }),
  ...(variant === 'delete' && {
    color: '#999',
    backgroundColor: '#f5f5f5',
    '&:hover': { color: '#e53e3e', backgroundColor: '#FEE2E2' },
  }),
  ...(variant === 'save' && {
    color: '#fff',
    backgroundColor: '#0F7F85',
    '&:hover': { backgroundColor: '#0a6b70' },
  }),
  ...(variant === 'cancel' && {
    color: '#666',
    backgroundColor: '#f0f0f0',
    '&:hover': { backgroundColor: '#e0e0e0' },
  }),
}));

export const MemoContent = styled(Typography)({
  fontSize: '13px',
  color: '#333',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  lineHeight: 1.7,
});

export const MemoItemMeta = styled(Typography)({
  display: 'flex',
  fontSize: '11px',
  color: '#aaa',
  marginTop: '8px',
});

// 폭을 내용(이름·연동 상태·연락처)에 맞춘다. 30% 고정이면 사이드 메뉴를 펼쳐 화면이 좁아질 때
// 연락처가 밀려 잘렸다. 좁아지는 몫은 메모·통계 카드가 나눠 가진다.
export const UserInfoCard = styled(Box)({
  flex: '0 0 auto',
  minWidth: '30%',
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  padding: '16px 24px',
  background: '#F3F9FA',
  borderRadius: '14px',
  border: '1px solid #E5E9EC',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '260px',
    height: '260px',
    pointerEvents: 'none',
  },
});

export const UserInfoLeft = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  minWidth: 0,
  position: 'relative',
  zIndex: 1,
});

export const UserInfoTopRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const UserInfoNameGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minWidth: 0,
  whiteSpace: 'nowrap',
});

export const UserInfoName = styled(Typography)({
  fontSize: '22px',
  fontWeight: 600,
  color: '#333333',
  letterSpacing: '-0.6px',
  lineHeight: 1.15,
});

// 나이/연락처는 항상 한 줄로 — 카드가 좁아져도 줄바꿈되지 않도록 nowrap.
export const UserInfoMetaRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  position: 'relative',
  zIndex: 1,
  flexWrap: 'nowrap',
  minWidth: 0,
});

export const SaleButtonIcon = styled('img')({
  width: '16px',
  height: '16px',
  objectFit: 'contain',
});

export const UserInfoMeta = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  position: 'relative',
  minWidth: 0,
  flexShrink: 0,
  '& + &::before': {
    content: '""',
    position: 'absolute',
    left: '-10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '1px',
    height: '16px',
    backgroundColor: '#D1D9E0',
  },
});

export const UserInfoLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 700,
  color: '#94A3B8',
  letterSpacing: '0.2px',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
});

export const UserInfoText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#1F2937',
  letterSpacing: '-0.1px',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
});

export const UserInfoGender = styled(Typography, {
  shouldForwardProp: prop => prop !== 'gender',
})(({ gender }) => {
  const isFemale = gender === 'FEMALE';
  const isMale = gender === 'MALE';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 700,
    padding: '6px 12px',
    borderRadius: '999px',
    color: isFemale ? '#C2185B' : isMale ? '#1565C0' : '#6B7280',
    backgroundColor: isFemale ? '#FCE4EC' : isMale ? '#E3F2FD' : '#F3F4F6',
    letterSpacing: '-0.1px',
    lineHeight: 1.2,
    '&::before': {
      content: '""',
      display: 'inline-block',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: isFemale ? '#E91E63' : isMale ? '#1976D2' : '#9CA3AF',
    },
  };
});

// 솔리드 단색 CTA. 이름(26px) 옆에 자리잡도록 살짝 큰 사이즈로 비율 조정.
// marginLeft: auto 로 카드 우측 끝으로 밀어냄.
export const SaleButton = styled('button')({
  border: 'none',
  outline: 'none',
  font: 'inherit',
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '44px',
  padding: '0 22px',
  fontSize: '18px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: '#4AB3BC',
  borderRadius: '8px',
  letterSpacing: '-0.2px',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#4AB3BC',
  },
  '&:active': {
    backgroundColor: '#4AB3BC',
  },
  '&:focus-visible': {
    outline: '2px solid #4AB3BC',
    outlineOffset: '2px',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'default',
    backgroundColor: '#4AB3BC',
    color: '#fff',
  },
});

// ============================================
// Message Modal
// ============================================
export const ModalContainer = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '10px',
  width: '606px',
  height: '580px',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

export const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: '#fff',
  background: '#202020',
  height: '52px',
  borderRadius: '10px 10px 0 0',
  padding: '0px 20px 0px 28px',
});

export const ModalTitle = styled(Typography)({
  fontSize: '16px',
});

export const ModalContent = styled(Box)({
  padding: '20px 28px',
});

export const CheckboxRow = styled(Box)({
  transform: 'translateX(-10px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
});

export const StyledTextarea = styled(TextareaAutosize)({
  width: '100%',
  marginTop: '10px',
  borderRadius: '4px',
  padding: '15px',
  border: '1px solid #e1e1e1',
});

export const ModalFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'end',
  marginRight: '20px',
  columnGap: '8px',
});

export const CancelButton = styled(Button)({
  borderRadius: '10px',
  border: 'none',
  background: '#c5c5c5',
  color: '#fff',
  '&:hover': {
    border: 'none',
    background: '#c5c5c5',
  },
});

export const SaveButton = styled(Button)({
  borderRadius: '10px',
});
